import { Component, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, AlertTitle, Box, Button, FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import Globe from "react-globe.gl";
import { feature } from "topojson-client";
import worldTopo from "world-atlas/countries-110m.json";

import { Disclose, EmptyState, Loading, Permission, Refusal, kernel } from "@lopie/kernel-ui";

import { CONFIDENCE, REGIONS, classify, surnameKey } from "./surnames.js";

/**
 * Surname Globe: people placed on a 3D globe by where their surname most
 * likely comes from.
 *
 * Everything comes from the KERNEL, read under this app's manifest and the
 * signed-in person's own tags:
 *  - Person records are the people;
 *  - Memory records are the answers of this app's platform agent
 *    (`surname-origin`). Each has subject = this app's type, content = a JSON
 *    answer, and derived_from = ["Person:<id>"] naming the person it is about.
 *    The kernel tags a memory from the record it was drawn from, so a person
 *    only ever sees answers about people they can already see.
 * This app holds no store and no model key of its own. Where the agent has
 * not answered yet, the built-in guesser in surnames.js is used, and every
 * person says which of the two placed them.
 */

const APP_TYPE = "app-surname-globe";
const AGENT = "surname-origin";
const PAGE = 200;
const MAX_PAGES = 50;
const COUNTRIES = feature(worldTopo, worldTopo.objects.countries).features;
const WEAK = new Set(["low", "weakest"]);

function esc(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
}

function plural(n, one, many) {
  return `${n} ${n === 1 ? one : many}`;
}

function surnameOf(fields) {
  const fam = (fields?.family_name || "").trim();
  if (fam) return { surname: fam, via: "family name" };
  const dn = (fields?.display_name || "").trim();
  if (dn && !dn.includes("@")) {
    const parts = dn.split(/\s+/);
    if (parts.length >= 2) return { surname: parts[parts.length - 1], via: "last word of display name" };
  }
  return null;
}

/** Every record of one collection the caller may see, following the kernel's paging rule. */
async function listAll(get, collection) {
  const items = [];
  let cursor = null;
  let more = false;
  let pages = 0;
  let first = null;
  do {
    const params = { limit: PAGE };
    if (cursor) params.cursor = cursor;
    const body = await get(`/${collection}`, params);
    if (!first) first = body;
    items.push(...(body.items || []));
    more = Boolean(body.has_more) && Boolean(body.cursor);
    cursor = more ? body.cursor : null;
    pages += 1;
  } while (more && pages < MAX_PAGES);
  return { items, pages, truncated: more, first };
}

/** This app's agent answers, keyed by Person id, from the Memory records the caller may see. */
function answersByPerson(memories) {
  const superseded = new Set(memories.map((m) => m.fields?.supersedes).filter(Boolean));
  const byPerson = {};
  let unreadable = 0;
  let total = 0;
  for (const m of memories) {
    const f = m.fields || {};
    if (f.subject !== APP_TYPE) continue;
    if (String(f.active) === "false" || superseded.has(m.id)) continue;
    total += 1;
    let c = null;
    try {
      c = JSON.parse(f.content);
    } catch {
      c = null;
    }
    if (!c || typeof c !== "object" || !c.surname) {
      unreadable += 1;
      continue;
    }
    for (const ref of [].concat(f.derived_from || [])) {
      const s = String(ref);
      if (!s.startsWith("Person:")) continue;
      (byPerson[s.slice("Person:".length)] ||= []).push({ ...c, memory_id: m.id, by: f.asserted_by });
    }
  }
  return { byPerson, unreadable, total };
}

class GlobeBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: null };
  }
  static getDerivedStateFromError(e) {
    return { failed: e };
  }
  render() {
    if (this.state.failed) {
      return (
        <Alert severity="warning" variant="outlined">
          <AlertTitle>The 3D globe could not start in this browser</AlertTitle>
          It needs WebGL, which this browser did not provide ({String(this.state.failed?.message || this.state.failed)}).
          The list beside it has exactly the same groups.
        </Alert>
      );
    }
    return this.props.children;
  }
}

function GlobeView({ groups, selected, onSelect }) {
  const wrap = useRef(null);
  const globe = useRef(null);
  const [width, setWidth] = useState(600);

  useEffect(() => {
    const el = wrap.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) => setWidth(Math.max(280, Math.floor(entry.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const height = Math.max(320, Math.min(560, width));
  const max = Math.max(1, ...groups.map((g) => g.people.length));
  const alt = (g) => 0.03 + 0.35 * (g.people.length / max);

  const byIso = useMemo(() => {
    const m = {};
    for (const g of groups) for (const iso of g.region.iso) (m[iso] ||= []).push(g);
    return m;
  }, [groups]);

  useEffect(() => {
    const g = globe.current;
    if (!g) return;
    g.pointOfView({ lat: 35, lng: 15, altitude: 2.3 });
    const c = g.controls();
    c.autoRotate = true;
    c.autoRotateSpeed = 0.35;
    const m = g.globeMaterial?.();
    if (m?.color?.set) m.color.set("#17324a");
  }, []);

  useEffect(() => {
    const g = globe.current;
    if (!g || !selected) return;
    const s = groups.find((x) => x.key === selected);
    if (!s) return;
    g.controls().autoRotate = false;
    g.pointOfView({ lat: s.region.lat, lng: s.region.lng, altitude: 1.7 }, 900);
  }, [selected, groups]);

  const isSel = (d) => (byIso[d.id] || []).some((g) => g.key === selected);
  const note = (g) => `${g.ai ? `<br/>${g.ai} placed by the AI agent` : ""}${g.weak ? `<br/>${g.weak} weak guesses` : ""}`;

  return (
    <Box
      ref={wrap}
      sx={{ width: "100%", minWidth: 0, bgcolor: "#0b1622", borderRadius: 1, overflow: "hidden", cursor: "grab" }}
      role="img"
      aria-label={`A 3D globe with ${plural(groups.length, "marker", "markers")}, one per surname origin. The same groups are listed beside it.`}
    >
      <Globe
        ref={globe}
        width={width}
        height={height}
        backgroundColor="rgba(0,0,0,0)"
        showAtmosphere
        atmosphereColor="#9fb7cc"
        atmosphereAltitude={0.15}
        polygonsData={COUNTRIES}
        polygonCapColor={(d) =>
          isSel(d) ? "rgba(255,255,255,0.8)" : byIso[d.id] ? "rgba(225,232,240,0.5)" : "rgba(140,160,180,0.14)"}
        polygonSideColor={() => "rgba(0,0,0,0)"}
        polygonStrokeColor={() => "rgba(200,215,230,0.35)"}
        polygonAltitude={(d) => (byIso[d.id] ? 0.012 : 0.004)}
        polygonsTransitionDuration={0}
        polygonLabel={(d) => {
          const gs = byIso[d.id] || [];
          const line = gs.length
            ? gs.map((g) => `${esc(g.region.name)}: ${plural(g.people.length, "person", "people")}${note(g)}`).join("<br/>")
            : "No surnames placed here";
          return `<div style="background:rgba(0,0,0,.75);color:#fff;padding:6px 8px;border-radius:4px;font:12px sans-serif"><b>${esc(d.properties?.name || "")}</b><br/>${line}${gs.length ? "<br/><i>a guess about surnames, not people</i>" : ""}</div>`;
        }}
        onPolygonClick={(d) => {
          const gs = byIso[d.id];
          if (gs?.length) onSelect(gs[0].key);
        }}
        pointsData={groups}
        pointLat={(g) => g.region.lat}
        pointLng={(g) => g.region.lng}
        pointAltitude={alt}
        pointRadius={(g) => (g.key === selected ? 1.0 : 0.6)}
        pointColor={(g) => (g.key === selected ? "#ffffff" : "#dfe7ef")}
        pointsMerge={false}
        pointLabel={(g) =>
          `<div style="background:rgba(0,0,0,.75);color:#fff;padding:6px 8px;border-radius:4px;font:12px sans-serif"><b>${esc(g.region.name)}</b><br/>${plural(g.people.length, "person", "people")}${note(g)}<br/><i>a guess about surnames, not people</i></div>`}
        onPointClick={(g) => onSelect(g.key)}
        labelsData={groups}
        labelLat={(g) => g.region.lat}
        labelLng={(g) => g.region.lng}
        labelAltitude={(g) => alt(g) + 0.01}
        labelText={(g) => String(g.people.length)}
        labelSize={1.1}
        labelDotRadius={0}
        labelColor={() => "rgba(255,255,255,0.95)"}
        labelResolution={2}
        onLabelClick={(g) => onSelect(g.key)}
      />
    </Box>
  );
}

function PersonLine({ p }) {
  return (
    <Box component="li" sx={{ mb: 0.75 }}>
      <Typography variant="body2">
        {p.name}
        {p.surname && p.surname !== p.name ? (
          <Typography component="span" variant="body2" color="text.secondary"> — {p.surname}</Typography>
        ) : null}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {[
          p.source === "ai" ? "AI agent" : p.source === "built-in" ? "built-in guesser" : null,
          p.confidence ? CONFIDENCE[p.confidence] : null,
          p.how,
          p.stale ? "the agent's answer was for an earlier surname, so it is not used" : null,
          p.via && p.via !== "family name" ? `surname taken from the ${p.via}` : null,
          p.held ? "surname was corrected by someone in this organisation, not the source" : null,
        ].filter(Boolean).join(" · ")}
      </Typography>
    </Box>
  );
}

function AgentStatus({ ai, sorted, onRetry }) {
  if (ai.phase === "loading") return null;
  if (ai.phase === "failed") {
    return (
      <Stack spacing={1}>
        <Typography variant="body2">
          The AI agent&apos;s answers (Memory records) could not be read, so every place below is from the built-in
          guesser. The kernel said:
        </Typography>
        <Refusal failure={ai.error} actor="Surname Globe" onRetry={onRetry} />
      </Stack>
    );
  }
  if (ai.phase === "noType") {
    return (
      <Alert severity="warning" variant="outlined">
        <AlertTitle>The kernel describes no Memory type</AlertTitle>
        GET /schema did not list a Memory collection, so there is nowhere the agent&apos;s answers could be. Every
        place below is from the built-in guesser.
      </Alert>
    );
  }
  if (!sorted) return null;
  return (
    <Stack spacing={0.5}>
      <Typography
        variant="body2"
        title="Counted now, over the people and the agent's Memory records the kernel returned to this app for you."
      >
        {sorted.answered} of {plural(sorted.withSurname, "person", "people")} with a surname have an answer from the
        AI agent · the rest are placed by the built-in guesser
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", maxWidth: "66ch" }}>
        The agent ({AGENT}) is run by the platform once an hour and only answers people it has not answered yet, so
        someone added since its last run waits for the next one. This app cannot see when it last ran.
        {sorted.stale ? ` ${plural(sorted.stale, "person's surname has", "people's surnames have")} changed since the agent answered.` : ""}
      </Typography>
      {sorted.unreadable ? (
        <Alert severity="warning" variant="outlined">
          {plural(sorted.unreadable, "answer from the agent was", "answers from the agent were")} not in the expected
          shape and {sorted.unreadable === 1 ? "was" : "were"} ignored.
        </Alert>
      ) : null}
      {ai.truncated ? (
        <Alert severity="warning" variant="outlined">
          Stopped after {ai.items.length} Memory records — the kernel says there are more, so some agent answers are
          missing here.
        </Alert>
      ) : null}
    </Stack>
  );
}

export default function App({ token, claims, onSignOut }) {
  const get = useMemo(() => kernel(token), [token]);
  const [state, setState] = useState({ phase: "loading" });
  const [ai, setAi] = useState({ phase: "loading" });
  const [selected, setSelected] = useState(null);
  const [showWeak, setShowWeak] = useState(true);

  const load = useCallback(async () => {
    setState({ phase: "loading" });
    setAi({ phase: "loading" });
    let schema;
    try {
      schema = await get("/schema");
    } catch (e) {
      setState({ phase: "failed", error: e });
      setAi({ phase: "failed", error: e });
      return;
    }
    const types = schema.types || [];
    const person = types.find((t) => t.type === "Person");
    const memory = types.find((t) => t.type === "Memory");

    // People and the agent's answers are read separately: one refused does not
    // make the other a failure, and each says which one it was.
    const people = (async () => {
      if (!person?.collection) {
        setState({ phase: "noType" });
        return;
      }
      try {
        const r = await listAll(get, person.collection);
        setState({
          phase: "ready",
          items: r.items,
          pages: r.pages,
          truncated: r.truncated,
          permitted: r.first?.permitted_tags || [],
          declared: r.first?.declared_tags || null,
        });
      } catch (e) {
        setState({ phase: "failed", error: e });
      }
    })();

    const answers = (async () => {
      if (!memory?.collection) {
        setAi({ phase: "noType" });
        return;
      }
      try {
        const r = await listAll(get, memory.collection);
        setAi({ phase: "ready", items: r.items, truncated: r.truncated });
      } catch (e) {
        setAi({ phase: "failed", error: e });
      }
    })();

    await Promise.all([people, answers]);
  }, [get]);

  useEffect(() => {
    load();
  }, [load]);

  const sorted = useMemo(() => {
    if (state.phase !== "ready") return null;
    const agent = ai.phase === "ready" ? answersByPerson(ai.items) : { byPerson: {}, unreadable: 0, total: 0 };
    const extra = {};
    const regionOf = (key) => REGIONS[key] || extra[key];
    const byRegion = {};
    const unknown = [];
    const shared = [];
    const nameless = [];
    const hidden = [];
    let answered = 0;
    let stale = 0;
    let withSurname = 0;
    for (const item of state.items) {
      const f = item.fields || {};
      const name = f.display_name || item.id;
      const s = surnameOf(f);
      const held = Boolean(item.held?.family_name);
      if (!s) {
        nameless.push({ id: item.id, name });
        continue;
      }
      withSurname += 1;
      const mine = agent.byPerson[item.id] || [];
      const o = [...mine].reverse().find((c) => surnameKey(c.surname) === surnameKey(s.surname));
      const isStale = !o && mine.length > 0;
      if (isStale) stale += 1;
      const p = { id: item.id, name, surname: s.surname, via: s.via, held, stale: isStale };
      let r;
      if (o) {
        answered += 1;
        let key = String(o.region || "").toLowerCase();
        if (!REGIONS[key]) {
          const place = String(o.place || "Elsewhere");
          key = `x:${place.toLowerCase()}`;
          if (!extra[key]) {
            const iso = String(o.iso || "").replace(/[^0-9]/g, "").slice(0, 3);
            extra[key] = {
              name: place,
              lat: Number(o.lat) || 0,
              lng: Number(o.lng) || 0,
              iso: iso ? [iso.padStart(3, "0")] : [],
            };
          }
        }
        r = {
          key,
          confidence: ["high", "medium", "low"].includes(o.confidence) ? o.confidence : "low",
          how: String(o.reason || "no reason given").slice(0, 300),
          source: "ai",
        };
      } else {
        r = classify(s.surname);
        if (r && !r.shared) r = { ...r, source: "built-in" };
      }
      if (!r) unknown.push(p);
      else if (r.shared) shared.push({ ...p, how: "common in several unrelated places, so not placed" });
      else {
        const placed = { ...p, how: r.how, confidence: r.confidence, source: r.source };
        if (!showWeak && WEAK.has(r.confidence)) hidden.push({ ...placed, how: `${regionOf(r.key).name} — ${r.how}` });
        else (byRegion[r.key] ||= []).push(placed);
      }
    }
    const groups = Object.entries(byRegion)
      .map(([key, people]) => ({
        key,
        region: regionOf(key),
        people,
        weak: people.filter((p) => WEAK.has(p.confidence)).length,
        ai: people.filter((p) => p.source === "ai").length,
      }))
      .sort((a, b) => b.people.length - a.people.length || a.region.name.localeCompare(b.region.name));
    const placed = groups.reduce((n, g) => n + g.people.length, 0);
    const weakCount = groups.reduce((n, g) => n + g.weak, 0);
    const aiCount = groups.reduce((n, g) => n + g.ai, 0);
    return {
      groups, unknown, shared, nameless, hidden, placed, weakCount, aiCount,
      answered, stale, withSurname, unreadable: agent.unreadable,
      off: unknown.length + shared.length + nameless.length + hidden.length,
    };
  }, [state, showWeak, ai]);

  const current = sorted?.groups.find((g) => g.key === selected) || null;
  const tagText = (state.permitted || []).length ? (state.permitted || []).join(", ") : "no tags";

  return (
    <Stack spacing={3} sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap" }}>
        <Typography variant="h1">Surname Globe</Typography>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Typography variant="body2" color="text.secondary">{claims?.username || ""}</Typography>
          <Typography
            component="button"
            variant="body2"
            onClick={onSignOut}
            sx={{ background: "none", border: 0, cursor: "pointer", color: "primary.main" }}
          >
            Sign out
          </Typography>
        </Stack>
      </Stack>

      <Permission
        gate={
          "Governed by this app's reviewed manifest — the people and the AI agent's answers below are that "
          + "ceiling intersected with your own groups. Where a surname comes from is a guess by this app's "
          + "agent or its built-in guesser, not a fact the kernel records."
        }
        groups={claims?.["cognito:groups"] || []}
        effective={{ permitted: state.permitted || [], declared: state.declared || null }}
      />

      {state.phase === "loading" ? <Loading label="Reading people from the kernel" minHeight="40vh" /> : null}

      {state.phase === "failed" ? <Refusal failure={state.error} actor="Surname Globe" onRetry={load} /> : null}

      {state.phase === "noType" ? (
        <Alert severity="warning" variant="outlined">
          <AlertTitle>The kernel describes no Person type</AlertTitle>
          GET /schema did not list a Person collection, so there is nobody for this app to place.
        </Alert>
      ) : null}

      {state.phase === "ready" && state.items.length === 0 ? (
        <EmptyState title="Nothing you may see" tagged={(state.permitted || []).join(", ")} />
      ) : null}

      {state.phase === "ready" && state.items.length > 0 && sorted ? (
        <>
          {state.truncated ? (
            <Alert severity="warning" variant="outlined">
              Stopped after {state.items.length} people ({state.pages} pages) — the kernel says there are more, so the
              globe is missing some.
            </Alert>
          ) : null}

          <Box sx={{ borderLeft: 3, borderColor: "primary.main", pl: 2 }}>
            <Typography
              sx={{ fontSize: { xs: "2.75rem", sm: "3.5rem" }, fontWeight: 300, lineHeight: 1.1 }}
              title={`Counted over every Person record the kernel returned to this app for you now, across ${plural(state.pages, "page", "pages")}.`}
            >
              {state.items.length}
            </Typography>
            <Typography variant="body2" sx={{ maxWidth: "66ch" }}>
              people you can see through this app · {sorted.placed} placed on the globe ({sorted.aiCount} by the AI
              agent{sorted.weakCount ? `, ${sorted.weakCount} weak guesses` : ""}) · {sorted.off} listed under
              &ldquo;Not on the globe&rdquo;
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", maxWidth: "66ch" }}>
              Records tagged {tagText} (plus untagged ones) — your own access limits this, not the size of the
              catalog. Places are a guess about the name&apos;s history, not the person&apos;s.
            </Typography>
          </Box>

          <AgentStatus ai={ai} sorted={sorted} onRetry={load} />

          <FormControlLabel
            control={<Switch checked={showWeak} onChange={(e) => setShowWeak(e.target.checked)} />}
            label={showWeak ? "Showing weak guesses on the globe" : "Weak guesses hidden — only strong and likely guesses shown"}
          />

          <Stack direction={{ xs: "column", md: "row" }} spacing={3} sx={{ minWidth: 0 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <GlobeBoundary>
                <GlobeView groups={sorted.groups} selected={selected} onSelect={setSelected} />
              </GlobeBoundary>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Drag to turn, scroll to zoom, click a marker or country. Each number is people in that group; taller
                markers are larger groups, relative to the largest one here (not to any fixed scale).
              </Typography>
            </Box>

            <Box sx={{ width: { xs: "100%", md: 360 }, flexShrink: 0, minWidth: 0 }}>
              <Typography variant="overline" sx={{ display: "block", color: "text.primary" }}>
                Surname origins · {plural(sorted.groups.length, "place", "places")}
              </Typography>
              {sorted.groups.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  None of these surnames could be placed{showWeak ? "" : " without weak guesses"}.
                </Typography>
              ) : null}
              <Stack spacing={0.5} sx={{ mb: 2 }}>
                {sorted.groups.map((g) => (
                  <Button
                    key={g.key}
                    variant={g.key === selected ? "outlined" : "text"}
                    color="inherit"
                    aria-pressed={g.key === selected}
                    onClick={() => setSelected(g.key === selected ? null : g.key)}
                    sx={{ justifyContent: "space-between", textTransform: "none", width: "100%", gap: 1 }}
                  >
                    <span style={{ textAlign: "left" }}>{g.region.name}{g.key === selected ? " · showing" : ""}</span>
                    <span style={{ whiteSpace: "nowrap" }}>
                      {g.people.length}
                      {g.weak ? <span style={{ opacity: 0.65 }}> · {g.weak} weak</span> : null}
                    </span>
                  </Button>
                ))}
              </Stack>

              {current ? (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">
                    {current.region.name} · {plural(current.people.length, "person", "people")}
                    {current.ai ? ` · ${current.ai} by the AI agent` : ""}
                    {current.weak ? ` · ${current.weak} weak` : ""}
                  </Typography>
                  <Box component="ul" sx={{ pl: 2.5, my: 1, maxHeight: 360, overflowY: "auto" }}>
                    {current.people.map((p) => <PersonLine key={p.id} p={p} />)}
                  </Box>
                </Box>
              ) : sorted.groups.length ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Pick a place to see who is in it and why.
                </Typography>
              ) : null}

              <Typography variant="overline" sx={{ display: "block", color: "text.primary" }}>
                Not on the globe · {sorted.off}
              </Typography>
              <Stack spacing={1}>
                {[
                  [sorted.hidden, "weak guesses, hidden by the switch"],
                  [sorted.shared, "surname common in several places"],
                  [sorted.unknown, "no guess possible"],
                  [sorted.nameless, "no surname recorded"],
                ].map(([list, label]) =>
                  list.length ? (
                    <Disclose key={label} label={`${list.length} · ${label}`}>
                      <Box component="ul" sx={{ pl: 2.5, my: 1, maxHeight: 300, overflowY: "auto" }}>
                        {list.map((p) => <PersonLine key={p.id} p={p} />)}
                      </Box>
                    </Disclose>
                  ) : null,
                )}
              </Stack>
            </Box>
          </Stack>

          <Disclose label="How a surname is placed (4 notes)">
            <Stack spacing={1.5} sx={{ maxWidth: "62ch" }}>
              <Typography variant="body2" color="text.secondary">
                The surname is the Person&apos;s family name, or the last word of their display name when no family
                name is recorded.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <b>AI agent</b>: this app declares one agent, {AGENT}, which the platform runs once an hour as this
                app and inside its reviewed manifest. It reads each person&apos;s surname and saves its answer — the
                likely origin, how sure it is, and a one-line reason — as a Memory record about that person. This app
                holds no AI key and no database of its own. The kernel tags each answer like the person it is about,
                so you only see answers about people you can already see. An answer can be wrong, and says so when
                it is unsure.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <b>Built-in guesser</b>, for people the agent has not answered yet: a list of common surnames, then
                spelling patterns, then — as a last resort — the last letter alone, which leans towards Western
                Europe. Each person says which one placed them and how sure it is.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This is a guess about a name, not a fact about a person. Names change through marriage, adoption,
                migration and anglicisation. It says nothing about anyone&apos;s nationality, ancestry or ethnicity.
                Somebody with different access would see a different globe.
              </Typography>
            </Stack>
          </Disclose>
        </>
      ) : null}
    </Stack>
  );
}
